import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { queryKeys } from '@/config';
import { notificationsApi } from '@/features/notifications';
import { logger } from '@/lib';

/** Web Push keys travel base64url-encoded; the browser wants raw bytes. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

const isSupported =
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

// The permission prompt can stay unanswered forever (swiped away on mobile,
// queued by a webview); without a cap the toggle then spins until a reload.
const PERMISSION_TIMEOUT_MS = 30_000;

function requestPermissionWithTimeout(): Promise<NotificationPermission> {
  return Promise.race([
    Notification.requestPermission(),
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('permission-timeout')), PERMISSION_TIMEOUT_MS);
    }),
  ]);
}

/**
 * Web push subscription for the PWA. The backend has had the VAPID stack all
 * along; this is the browser half that was missing, so PUSH notifications
 * actually leave the server.
 *
 * "Subscribed" is an ACCOUNT fact, not a browser fact: the browser hands the
 * same subscription to whoever is logged in, so a subscription is counted only
 * when its endpoint matches the one stored on the current account. On a shared
 * device the previous user's subscription therefore reads as "not subscribed",
 * the offer comes back, and subscribing hands the device over to this account
 * (the backend clears the endpoint off the previous holder).
 */
export function usePushSubscription() {
  const { toast } = useToast();
  const { t } = useTranslation('notifications');
  const queryClient = useQueryClient();
  const [browserEndpoint, setBrowserEndpoint] = useState<string | null>(null);
  const [isCheckingBrowser, setIsCheckingBrowser] = useState(isSupported);

  const { data: vapid } = useQuery({
    queryKey: queryKeys.push.vapidPublicKey,
    queryFn: notificationsApi.getVapidPublicKey,
    enabled: isSupported,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const { data: accountSubscription, isLoading: isCheckingAccount } = useQuery({
    queryKey: queryKeys.push.subscription,
    queryFn: notificationsApi.getPushSubscription,
    enabled: isSupported,
  });

  useEffect(() => {
    if (!isSupported) return;

    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => setBrowserEndpoint(subscription?.endpoint ?? null))
      .catch((error) => logger.error('Provera push pretplate nije uspela', error))
      .finally(() => setIsCheckingBrowser(false));
  }, []);

  const isSubscribed = !!browserEndpoint && accountSubscription?.endpoint === browserEndpoint;

  const subscribeMutation = useMutation({
    // The default networkMode pauses the mutation while offline: the click
    // looks like a no-op and then fires whenever connectivity returns. Run it
    // immediately instead, so the failed call surfaces the error toast.
    networkMode: 'always',
    mutationFn: async () => {
      if (!vapid?.publicKey) {
        throw new Error('VAPID ključ nije konfigurisan');
      }

      const permission = await requestPermissionWithTimeout();
      if (permission !== 'granted') {
        throw new Error('permission-denied');
      }

      const registration = await navigator.serviceWorker.ready;
      // subscribe() returns the EXISTING subscription when the device already
      // has one for this key pair, so remember whether it was ours to destroy
      // before treating a failed store as something to roll back.
      const preexisting = await registration.pushManager.getSubscription();
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey) as BufferSource,
      });

      const json = subscription.toJSON();
      try {
        await notificationsApi.pushSubscribe({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: json.keys?.p256dh ?? '',
            auth: json.keys?.auth ?? '',
          },
        });
      } catch (error) {
        // A browser subscription the backend never stored reads as "enabled"
        // on every later launch while no push can ever arrive; roll it back so
        // the offer comes back instead. Only a subscription this call created
        // is rolled back: a pre-existing one may be live for another account,
        // and if the server actually committed despite the client-side error,
        // the endpoint comparison self-heals on the next fetch.
        if (!preexisting) {
          await subscription.unsubscribe().catch(() => undefined);
        }
        throw error;
      }

      return subscription.endpoint;
    },
    onSuccess: (endpoint) => {
      setBrowserEndpoint(endpoint);
      queryClient.setQueryData(queryKeys.push.subscription, { endpoint });
      toast({ title: t('push.subscribed'), variant: 'success' });
    },
    onError: (error: Error) => {
      const title =
        error.message === 'permission-denied'
          ? t('push.denied')
          : error.message === 'permission-timeout'
            ? t('push.timeout')
            : t('push.failed');
      toast({ title, variant: 'error' });
    },
  });

  const unsubscribeMutation = useMutation({
    networkMode: 'always',
    mutationFn: async () => {
      // Backend first: a failed call then leaves a live browser subscription
      // behind a cleared server record (which delivers nothing), instead of a
      // dead endpoint the server keeps pushing to.
      await notificationsApi.pushUnsubscribe();
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      await subscription?.unsubscribe().catch(() => undefined);
    },
    onSuccess: () => {
      setBrowserEndpoint(null);
      queryClient.setQueryData(queryKeys.push.subscription, { endpoint: null });
      toast({ title: t('push.unsubscribed'), variant: 'success' });
    },
    onError: () => toast({ title: t('push.failed'), variant: 'error' }),
  });

  const toggle = useCallback(() => {
    if (isSubscribed) {
      unsubscribeMutation.mutate();
    } else {
      subscribeMutation.mutate();
    }
  }, [isSubscribed, subscribeMutation, unsubscribeMutation]);

  return {
    isSupported,
    isSubscribed,
    isChecking: isCheckingBrowser || (isSupported && isCheckingAccount),
    isPending: subscribeMutation.isPending || unsubscribeMutation.isPending,
    hasKey: !!vapid?.publicKey,
    toggle,
  };
}
